import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { formatCurrency, type CurrencyCode, CURRENCIES } from './formatters';

interface CurrencyCtx {
  code: CurrencyCode;
  locale: string;
  symbol: string;
  setCurrency: (c: CurrencyCode) => void;
  format: (value: number) => string;
}

const Ctx = createContext<CurrencyCtx | null>(null);

export function CurrencyProvider({
  children,
  defaultCurrency = 'INR' as CurrencyCode,
}: {
  children: ReactNode;
  defaultCurrency?: CurrencyCode;
}) {
  const [code, setCode] = useState<CurrencyCode>(defaultCurrency);

  const value = useMemo<CurrencyCtx>(() => {
    const cfg = CURRENCIES.find((c) => c.code === code) ?? CURRENCIES[0];
    return {
      code,
      locale: cfg.locale,
      symbol: cfg.symbol,
      setCurrency: setCode,
      format: (v) => formatCurrency(v, { locale: cfg.locale, currency: cfg.code }),
    };
  }, [code]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCurrency() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useCurrency must be used within CurrencyProvider');
  return v;
}
